import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { paymentService } from '../../services/paymentService';
import { photographerService } from '../../services/photographerService';
import AppIcon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Toast from '../../components/ui/Toast';
import { formatNumber } from '../../utils/formatNumber';
import { getPhotographyTypeLabelAr } from '../../utils/bookingLabels';
const SAUDI_IBAN_LENGTH = 24; // SA + 22 رقم

/** تنسيق الآيبان للعرض مع مسافات (مثل تطبيقات البنوك) - يقبل الإدخال مع أو بدون مسافات */
const formatIbanForDisplay = (value) => {
  const cleaned = (value || '').replace(/\s/g, '').replace(/[^SA0-9]/g, '').slice(0, SAUDI_IBAN_LENGTH);
  if (cleaned.length <= 4) return cleaned;
  return cleaned.slice(0, 4) + ' ' + cleaned.slice(4).replace(/(.{4})/g, '$1 ').trim();
};

const validateSaudiIban = (value, required = false) => {
  const cleaned = (value || '').trim().replace(/\s/g, '');
  if (!cleaned) return required ? `الآيبان مطلوب (${SAUDI_IBAN_LENGTH} حرفاً: SA + 22 رقم)` : null;
  if (cleaned.length < SAUDI_IBAN_LENGTH) return `الآيبان يتكون من ${SAUDI_IBAN_LENGTH} حرفاً (SA + 22 رقم). الموجود: ${cleaned.length}`;
  if (cleaned.length > SAUDI_IBAN_LENGTH) return `الآيبان يجب أن يكون ${SAUDI_IBAN_LENGTH} حرفاً فقط. الموجود: ${cleaned.length}`;
  if (!/^SA[0-9]{22}$/.test(cleaned)) return 'الآيبان يبدأ بـ SA متبوعاً بـ 22 رقماً فقط';
  return null;
};

const Payments = () => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [savingBank, setSavingBank] = useState(false);
  const [toast, setToast] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [bankInfo, setBankInfo] = useState({
    bankName: '',
    bankAccountHolderName: '',
    iban: '',
  });
  const [ibanError, setIbanError] = useState(null);

  useEffect(() => {
    if (userProfile?.id) {
      loadData();
    }
  }, [userProfile?.id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [earningsData, profileData] = await Promise.all([
        paymentService.getPhotographerEarningsFromBookings(),
        photographerService.getPhotographer(userProfile?.id),
      ]);
      setEarnings(earningsData);
      const rawIban = profileData?.iban || '';
      setBankInfo({
        bankName: profileData?.bankName || profileData?.bank_name || '',
        bankAccountHolderName: profileData?.bankAccountHolderName || profileData?.bank_account_holder_name || '',
        iban: rawIban ? formatIbanForDisplay(rawIban) : '',
      });
      setIbanError(validateSaudiIban(profileData?.iban));
    } catch (err) {
      console.error('Failed to load payments data:', err);
      setToast({ type: 'error', message: 'فشل تحميل بيانات المدفوعات' });
      setEarnings({ summary: { totalEarnings: 0, totalCommission: 0, totalRevenue: 0 }, transactions: [], commissionRate: 20 });
    } finally {
      setLoading(false);
    }
  };

  const handleIbanChange = (e) => {
    const raw = e.target.value;
    if (!raw || !raw.trim()) {
      setBankInfo((prev) => ({ ...prev, iban: '' }));
      setIbanError(null);
      return;
    }
    let v = raw.toUpperCase().replace(/\s/g, '').replace(/[^SA0-9]/g, '');
    if (/^[0-9]+/.test(v)) v = 'SA' + v.replace(/[^0-9]/g, '');
    else if (v.startsWith('A')) v = 'SA' + v.slice(1).replace(/[^0-9]/g, '');
    else if (v.startsWith('S') && v.length > 1) v = 'SA' + v.slice(2).replace(/[^0-9]/g, '');
    else if (v.startsWith('S')) v = 'S';
    v = v.slice(0, SAUDI_IBAN_LENGTH);
    const displayValue = formatIbanForDisplay(v);
    setBankInfo((prev) => ({ ...prev, iban: displayValue }));
    setIbanError(validateSaudiIban(v));
  };

  const ibanCleaned = bankInfo.iban.trim().replace(/\s/g, '');
  const isIbanValid = ibanCleaned.length >= SAUDI_IBAN_LENGTH && !validateSaudiIban(bankInfo.iban);

  const handleSaveBankInfo = async (e) => {
    e?.preventDefault();
    if (!userProfile?.id) return;
    const err = validateSaudiIban(bankInfo.iban, true);
    if (err) {
      setIbanError(err);
      setToast({ type: 'error', message: err });
      return;
    }
    setSavingBank(true);
    setToast(null);
    try {
      await photographerService.updateBankInfo(userProfile.id, {
        bankName: bankInfo.bankName.trim() || null,
        bankAccountHolderName: bankInfo.bankAccountHolderName.trim() || null,
        iban: bankInfo.iban.trim().replace(/\s/g, '') || null,
      });
      setToast({ type: 'success', message: 'تم حفظ المعلومات البنكية بنجاح' });
    } catch (err) {
      console.error('Failed to save bank info:', err);
      setToast({ type: 'error', message: err?.message || 'فشل حفظ المعلومات البنكية' });
    } finally {
      setSavingBank(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[280px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent mx-auto mb-3" />
          <p className="text-slate-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  const summary = earnings?.summary || { totalEarnings: 0, totalCommission: 0, totalRevenue: 0 };
  const transactions = earnings?.transactions || [];
  const commissionRate = earnings?.commissionRate ?? 20;

  return (
    <div className="space-y-6">
      <h1 className="text-xl md:text-2xl font-bold text-slate-900" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
        المدفوعات
      </h1>

      {/* قسم الأرباح */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-4 md:p-6 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
            <AppIcon name="TrendingUp" size={22} className="text-primary" />
            الأرباح
          </h2>
          <p className="text-sm text-slate-600 mt-1">ملخص أرباحك من الحجوزات المدفوعة (بعد خصم عمولة المنصة {commissionRate}%)</p>
        </div>
        <div className="p-4 md:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-primary/5 rounded-xl p-4 border border-primary/20">
              <p className="text-sm text-slate-600 mb-1">إجمالي الإيرادات</p>
              <p className="text-xl font-bold text-slate-900">{formatNumber(summary.totalRevenue)} ر.س</p>
            </div>
            <div className="bg-amber-50 rounded-xl p-4 border border-amber-200/50">
              <p className="text-sm text-slate-600 mb-1">عمولة المنصة</p>
              <p className="text-xl font-bold text-amber-800">{formatNumber(summary.totalCommission)} ر.س</p>
            </div>
            <div className="bg-success/10 rounded-xl p-4 border border-success/20">
              <p className="text-sm text-slate-600 mb-1">صافي أرباحك</p>
              <p className="text-xl font-bold text-success">{formatNumber(summary.totalEarnings)} ر.س</p>
            </div>
          </div>

          <h3 className="text-base font-semibold text-slate-800 mb-1" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
            سجل المعاملات
          </h3>
          <p className="text-xs text-slate-500 mb-3">"تم التحويل" = تم تحويل المبلغ لك من المشرف. "قيد الانتظار" = لم يُصرف بعد.</p>
          {transactions.length === 0 ? (
            <div className="text-center py-8 rounded-xl bg-slate-50 border border-slate-100">
              <AppIcon name="Receipt" size={40} className="text-slate-300 mx-auto mb-2" />
              <p className="text-slate-600">لا توجد معاملات حتى الآن</p>
              <p className="text-sm text-slate-500 mt-1">ستظهر هنا بعد إتمام حجوزات مدفوعة</p>
            </div>
          ) : (
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-right">
                    <th className="py-3 px-2 font-semibold text-slate-700">التاريخ</th>
                    <th className="py-3 px-2 font-semibold text-slate-700">نوع الخدمة</th>
                    <th className="py-3 px-2 font-semibold text-slate-700">المبلغ</th>
                    <th className="py-3 px-2 font-semibold text-slate-700">العمولة</th>
                    <th className="py-3 px-2 font-semibold text-slate-700">صافيك</th>
                    <th className="py-3 px-2 font-semibold text-slate-700">حالة التحويل</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((t) => (
                    <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="py-3 px-2 text-slate-600">{formatDate(t.date)}</td>
                      <td className="py-3 px-2 text-slate-800">{t.photographyType ? getPhotographyTypeLabelAr(t.photographyType) : '—'}</td>
                      <td className="py-3 px-2 font-medium">{formatNumber(t.amount)} ر.س</td>
                      <td className="py-3 px-2 text-amber-700">-{formatNumber(t.commission)} ر.س</td>
                      <td className="py-3 px-2 font-semibold text-success">+{formatNumber(t.netAmount)} ر.س</td>
                      <td className="py-3 px-2">
                        {t.payoutStatus === 'paid' ? (
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-green-100 text-green-700 text-xs font-medium">
                            <AppIcon name="CheckCircle" size={14} />
                            تم التحويل
                          </span>
                        ) : t.payoutStatus === 'refunded' ? (
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-red-50 text-red-600 text-xs font-medium">
                            <AppIcon name="RotateCcw" size={14} />
                            مُسترد للعميل
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-amber-50 text-amber-700 text-xs font-medium">
                            <AppIcon name="Clock" size={14} />
                            قيد الانتظار
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* قسم المعلومات البنكية */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-4 md:p-6 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
            <AppIcon name="Landmark" size={22} className="text-primary" />
            المعلومات البنكية
          </h2>
          <p className="text-sm text-slate-600 mt-1">أدخل بيانات الحساب البنكي لاستلام تحويل الأرباح. تظهر للمشرف لأغراض التحويل فقط.</p>
        </div>
        <form onSubmit={handleSaveBankInfo} className="p-4 md:p-6 space-y-4">
          <Input
            label="اسم البنك"
            value={bankInfo.bankName}
            onChange={(e) => setBankInfo((prev) => ({ ...prev, bankName: e.target.value }))}
            placeholder="مثال: البنك الأهلي"
            className="max-w-md"
          />
          <Input
            label="اسم صاحب الحساب كما في البنك"
            value={bankInfo.bankAccountHolderName}
            onChange={(e) => setBankInfo((prev) => ({ ...prev, bankAccountHolderName: e.target.value }))}
            placeholder="الاسم بالكامل كما يظهر في الحساب"
            className="max-w-md"
          />
          <Input
            label="رقم الآيبان (IBAN)"
            value={bankInfo.iban}
            onChange={handleIbanChange}
            placeholder="SA00 0000 0000 0000 0000 0000"
            maxLength={31}
            dir="ltr"
            className="max-w-md font-mono"
            error={ibanError}
          />
          <p className="text-xs text-slate-500 -mt-1">
            آيبان رسمي: 24 حرفاً (SA + 22 رقم)
          </p>
          <Button
            type="submit"
            loading={savingBank}
            iconName="Save"
            size="lg"
            disabled={!isIbanValid}
            title={!isIbanValid ? 'أدخل آيبان صحيح (24 حرفاً: SA + 22 رقم)' : undefined}
          >
            حفظ المعلومات البنكية
          </Button>
        </form>
      </div>

      {toast && (
        <Toast
          notification={{
            title: toast.type === 'success' ? 'تم بنجاح' : 'خطأ',
            message: toast.message,
            icon: toast.type === 'success' ? 'CheckCircle' : 'AlertCircle',
            iconColor: toast.type === 'success' ? '#22c55e' : '#ef4444',
          }}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default Payments;
